import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs) {
  return twMerge(clsx(inputs));
}

// Arial character width map (normalized to 1000 units per em)
const ARIAL_CHAR_WIDTHS = {
  ' ': 278, '!': 278, '"': 355, '#': 556, '$': 556, '%': 889, '&': 667, '\'': 222,
  '(': 333, ')': 333, '*': 389, '+': 584, ',': 278, '-': 333, '.': 278, '/': 278,
  '0': 556, '1': 556, '2': 556, '3': 556, '4': 556, '5': 556, '6': 556, '7': 556,
  '8': 556, '9': 556, ':': 278, ';': 278, '<': 584, '=': 584, '>': 584, '?': 556,
  '@': 1015, 'A': 667, 'B': 667, 'C': 722, 'D': 722, 'E': 667, 'F': 611, 'G': 778,
  'H': 722, 'I': 278, 'J': 500, 'K': 667, 'L': 556, 'M': 833, 'N': 722, 'O': 778,
  'P': 667, 'Q': 778, 'R': 722, 'S': 667, 'T': 611, 'U': 722, 'V': 667, 'W': 944,
  'X': 667, 'Y': 667, 'Z': 611, '[': 278, '\\': 278, ']': 278, '^': 469, '_': 556,
  '`': 333, 'a': 556, 'b': 556, 'c': 500, 'd': 556, 'e': 556, 'f': 278, 'g': 556,
  'h': 556, 'i': 222, 'j': 222, 'k': 500, 'l': 222, 'm': 833, 'n': 556, 'o': 556,
  'p': 556, 'q': 556, 'r': 333, 's': 500, 't': 278, 'u': 556, 'v': 500, 'w': 722,
  'x': 500, 'y': 500, 'z': 500, '{': 334, '|': 260, '}': 334, '~': 584,
  'æ': 889, 'ø': 611, 'å': 556, 'Æ': 1000, 'Ø': 778, 'Å': 667,
  'é': 556, '–': 556, '—': 1000, '…': 1000, '”': 355, '“': 355
};

export function getArialPixelWidth(text, fontSize = 20) {
  let units = 0;
  for (const char of String(text || '')) {
    units += ARIAL_CHAR_WIDTHS[char] || 556;
  }
  return (units / 1000) * fontSize;
}

export function clampToPixelWidth(text, maxPixels, fontSize, suffix = '') {
  const str = String(text || '').trim();
  if (getArialPixelWidth(str, fontSize) <= maxPixels) {
    return str;
  }
  const suffixWidth = getArialPixelWidth(suffix, fontSize);
  const words = str.split(' ');
  let result = '';
  for (let i = 0; i < words.length; i++) {
    const candidate = result ? `${result} ${words[i]}` : words[i];
    if (getArialPixelWidth(candidate, fontSize) + suffixWidth > maxPixels) {
      break;
    }
    result = candidate;
  }
  return result ? `${result}${suffix}` : str.slice(0, Math.floor(maxPixels / (fontSize * 0.55)));
}

function setOrCreateMeta(selector, attributeName, attributeValue, content) {
  let el = document.querySelector(selector);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attributeName, attributeValue);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

export function updateMeta(title, description, customCanonical) {
  // Max 512px for title in Google SERP (at 20px font size)
  const safeTitle = title ? clampToPixelWidth(title, 512, 20) : '';
  
  // Max 970px for description in Google SERP (at 14px font size)
  const safeDescription = description !== undefined ? clampToPixelWidth(description, 970, 14) : undefined;

  if (safeTitle) {
    document.title = safeTitle;
    setOrCreateMeta('meta[property="og:title"]', 'property', 'og:title', safeTitle);
    setOrCreateMeta('meta[name="twitter:title"]', 'name', 'twitter:title', safeTitle);
  }

  if (safeDescription !== undefined) {
    setOrCreateMeta('meta[name="description"]', 'name', 'description', safeDescription);
    setOrCreateMeta('meta[property="og:description"]', 'property', 'og:description', safeDescription);
    setOrCreateMeta('meta[name="twitter:description"]', 'name', 'twitter:description', safeDescription);
  }

  // Ensure og:type, og:image, and twitter:card
  setOrCreateMeta('meta[property="og:type"]', 'property', 'og:type', 'website');
  setOrCreateMeta('meta[name="twitter:card"]', 'name', 'twitter:card', 'summary_large_image');
  setOrCreateMeta(
    'meta[property="og:image"]',
    'property',
    'og:image',
    'https://assets.auxodev.dk/assets/57eb0739-2c0e-4813-8144-be81ba542e2d/thellefsen-regnskab-logo.png'
  );

  // Canonical URL
  let canonicalEl = document.querySelector('link[rel="canonical"]');
  if (!canonicalEl) {
    canonicalEl = document.createElement('link');
    canonicalEl.setAttribute('rel', 'canonical');
    document.head.appendChild(canonicalEl);
  }
  const canonicalHref = customCanonical || window.location.href.split('?')[0].split('#')[0];
  canonicalEl.setAttribute('href', canonicalHref);
}

